import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { apiGet, apiPut } from '@/context/AuthContext';
import { DEFAULT_CONTENT, mergeContent } from '@/lib/content';
import { useToast } from '@/hooks/use-toast';
import { Plus, Save, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

const tabs = [
  { id: 'landing', label: 'Forside' },
  { id: 'event_information', label: 'Eventinformation' },
  { id: 'contact', label: 'Kontakt' },
  { id: 'accommodation', label: 'Overnatning' },
  { id: 'faq', label: 'Spørgsmål og svar' },
];

const fields = {
  landing: [
    ['event_label', 'Eventlabel'],
    ['heading', 'Overskrift'],
    ['description', 'Beskrivelse', 'textarea'],
    ['invitation_placeholder', 'Placeholder til invitationskode'],
    ['access_button_label', 'Knaptekst'],
  ],
  event_information: [
    ['title', 'Titel'],
    ['short_description', 'Kort beskrivelse', 'textarea'],
    ['date_text', 'Dato'],
    ['time_text', 'Tid'],
    ['location_text', 'Sted'],
    ['practical_information', 'Praktisk information', 'textarea'],
  ],
  contact: [
    ['contact_name', 'Kontaktperson'],
    ['contact_email', 'Kontakt-e-mail'],
    ['contact_text', 'Kontakttekst', 'textarea'],
  ],
  accommodation: [
    ['heading', 'Overskrift'],
    ['description', 'Beskrivelse', 'textarea'],
    ['practical_information', 'Praktisk information', 'textarea'],
  ],
};

export default function AdminContent() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('event_information');
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    apiGet('/api/admin/content')
      .then((res) => setContent(mergeContent(res.data.content)))
      .catch(() => {
        toast({ title: 'Kunne ikke hente indhold', description: 'Fallback-indhold vises.', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  const activeFields = useMemo(() => fields[activeTab] || [], [activeTab]);

  const updateField = (section, key, value) => {
    setContent((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value,
      },
    }));
    setDirty(true);
  };

  const updateFaq = (id, key, value) => {
    setContent((current) => ({
      ...current,
      faq: current.faq.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    }));
    setDirty(true);
  };

  const addFaq = () => {
    setContent((current) => ({
      ...current,
      faq: [
        ...current.faq,
        {
          id: `faq-${Date.now()}`,
          question: '',
          answer: '',
          sort_order: current.faq.length + 1,
          enabled: true,
        },
      ],
    }));
    setDirty(true);
  };

  const removeFaq = (id) => {
    if (!window.confirm('Slet dette spørgsmål?')) return;
    setContent((current) => ({
      ...current,
      faq: current.faq.filter((item) => item.id !== id).map((item, index) => ({ ...item, sort_order: index + 1 })),
    }));
    setDirty(true);
  };

  const moveFaq = (id, direction) => {
    setContent((current) => {
      const next = [...current.faq];
      const index = next.findIndex((item) => item.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return {
        ...current,
        faq: next.map((item, itemIndex) => ({ ...item, sort_order: itemIndex + 1 })),
      };
    });
    setDirty(true);
  };

  const save = async () => {
    const invalidFaq = content.faq.some((item) => item.enabled && (!item.question.trim() || !item.answer.trim()));
    if (invalidFaq) {
      toast({ title: 'FAQ mangler tekst', description: 'Udfyld både spørgsmål og svar, eller slå punktet fra.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const saveContent = mergeContent(content);
      const res = await apiPut('/api/admin/content', { content: saveContent });
      setContent(mergeContent(res.data.content));
      setDirty(false);
      toast({ title: 'Indhold gemt', description: 'Gæstesiderne bruger nu den nye tekst.' });
    } catch (err) {
      toast({ title: 'Kunne ikke gemme indhold', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-400">Indlæser indhold...</div>;

  return (
    <div className="animate-fade-in space-y-6" data-testid="admin-content-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Indhold</h1>
          <p className="text-sm text-slate-400 mt-1">Rediger gæstevendt tekst uden kodeændringer.</p>
        </div>
        <Button onClick={save} disabled={saving || !dirty} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white">
          <Save className="w-4 h-4" /> {saving ? 'Gemmer...' : 'Gem indhold'}
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`min-h-10 whitespace-nowrap rounded-md px-3 text-sm transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== 'faq' ? (
        <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 space-y-4">
          {activeFields.map(([key, label, type]) => (
            <label key={key} className="block">
              <span className="text-sm text-slate-400 block mb-1.5">{label}</span>
              {type === 'textarea' ? (
                <textarea
                  rows={key === 'practical_information' ? 6 : 4}
                  value={content[activeTab]?.[key] || ''}
                  onChange={(e) => updateField(activeTab, key, e.target.value)}
                  data-testid={`content-${activeTab}-${key}`}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none resize-y"
                />
              ) : (
                <input
                  type={key.includes('email') ? 'email' : 'text'}
                  value={content[activeTab]?.[key] || ''}
                  onChange={(e) => updateField(activeTab, key, e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none"
                />
              )}
            </label>
          ))}
        </section>
      ) : (
        <section className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={addFaq} className="w-full sm:w-auto bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700">
              <Plus className="w-4 h-4" /> Tilføj spørgsmål
            </Button>
          </div>
          {content.faq.map((item, index) => (
            <div key={item.id} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={item.enabled} onChange={(e) => updateFaq(item.id, 'enabled', e.target.checked)} />
                  Vist
                </label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={index === 0} onClick={() => moveFaq(item.id, -1)} className="border-slate-700 bg-slate-900 text-slate-300">
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={index === content.faq.length - 1} onClick={() => moveFaq(item.id, 1)} className="border-slate-700 bg-slate-900 text-slate-300">
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => removeFaq(item.id)} className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <input
                type="text"
                placeholder="Spørgsmål"
                value={item.question}
                onChange={(e) => updateFaq(item.id, 'question', e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none"
              />
              <textarea
                rows={3}
                placeholder="Svar"
                value={item.answer}
                onChange={(e) => updateFaq(item.id, 'answer', e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none resize-y"
              />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
